import { removeTrapFocus, trapFocus } from 'dom-utils';

const sectionHeader = document.querySelector('.section-header');
const navBtns = document.querySelectorAll('.nav-trigger-btn');
const parentAnchors = document.querySelectorAll('.parent-anchor');
const backAnchors = document.querySelectorAll('.back-anchor');
const overlay = document.querySelector('.overlay');
const nav = document.querySelector('.nav');
const navWrapper = document.querySelector('.nav__wrapper');
const navMenuCarousels = document.querySelectorAll(
  '.nav-menu-carousel .products__container'
);
const header = document.querySelector('.header');
const headerTop = document.querySelector('.header-top');
const iconNav = document.querySelector('.icon-nav');
const smallTabletAndUp = window.matchMedia('(min-width: 768px)');
const twoRowHeader = window.matchMedia(
  '(min-width: 768px) and (max-width: 1239px)'
);

/**
 * Allows the mobile navigation product carousels to be dragged with a mouse.
 * Touch scrolling remains native so vertical gestures continue to work normally.
 */
function setupNavMenuCarouselDragging() {
  if (!navMenuCarousels || navMenuCarousels.length === 0) return;
  navMenuCarousels.forEach(productsContainer => {
    let isDragging = false;
    let hasDragged = false;
    let startX = 0;
    let startScrollLeft = 0;

    function handleMouseMove(e) {
      if (!isDragging) return;
      const distance = e.clientX - startX;

      if (Math.abs(distance) > 5) {
        if (!hasDragged) {
          hasDragged = true;
          productsContainer.classList.add('is-dragging');
        }
        e.preventDefault();
      }

      if (hasDragged) {
        productsContainer.scrollLeft = startScrollLeft - distance;
      }
    }

    function stopDragging() {
      if (!isDragging) return;

      isDragging = false;
      productsContainer.classList.remove('is-dragging');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('blur', stopDragging);

      if (hasDragged) {
        setTimeout(() => {
          hasDragged = false;
        }, 0);
      }
    }

    productsContainer.addEventListener('mousedown', e => {
      if (e.button !== 0) return;

      isDragging = true;
      hasDragged = false;
      startX = e.clientX;
      startScrollLeft = productsContainer.scrollLeft;
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', stopDragging);
      window.addEventListener('blur', stopDragging);
    });

    productsContainer.addEventListener('dragstart', e => e.preventDefault());
    productsContainer.addEventListener(
      'click',
      e => {
        if (!hasDragged) return;

        e.preventDefault();
        e.stopPropagation();
        hasDragged = false;
      },
      true
    );
  });
}

/**
 * Keeps the DOM focus order aligned with the responsive visual order.
 * The two-row header places the icon controls before navigation at 1024px-1239px. Mobile and
 * the single-row header place navigation before the icon controls.
 */
function syncHeaderDomOrder() {
  if (!headerTop || !nav || !iconNav) return;

  if (twoRowHeader.matches && iconNav.nextElementSibling !== nav) {
    headerTop.insertBefore(iconNav, nav);
  } else if (!twoRowHeader.matches && nav.nextElementSibling !== iconNav) {
    headerTop.insertBefore(nav, iconNav);
  }
}

/**
 * Sets all interactive elements within a sub-nav as focusable or non-focusable.
 * Saves the original tabindex via a data attribute so it can be restored later.
 * Elements already explicitly set to tabindex="-1" are left untouched.
 *
 * @param {HTMLElement} subNav - The .sub-nav element
 * @param {boolean} focusable - Whether elements should receive keyboard focus
 */
function setSubNavFocusable(subNav, focusable) {
  if (!subNav) return;

  if (focusable) {
    const savedTabindexElements = subNav.querySelectorAll(
      '[data-nav-tabindex]'
    );
    if (!savedTabindexElements.length) return;

    savedTabindexElements.forEach(el => {
      const saved = el.getAttribute('data-nav-tabindex');
      if (saved === 'none') {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', saved);
      }
      el.removeAttribute('data-nav-tabindex');
    });
  } else {
    const focusableElements = subNav.querySelectorAll(
      'a, button, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusableElements.length) return;

    focusableElements.forEach(el => {
      // Skip elements already explicitly non-focusable
      if (el.getAttribute('tabindex') === '-1') return;
      const current = el.getAttribute('tabindex');
      el.setAttribute('data-nav-tabindex', current !== null ? current : 'none');
      el.setAttribute('tabindex', '-1');
    });
  }
}

/**
 * Updates the navigation submenu's focusability when it exists.
 *
 * @param {boolean} focusable - Whether elements should receive keyboard focus
 */
function setNavigationSubNavFocusable(focusable) {
  const subNavs = document.querySelectorAll('.sub-nav');
  if (!subNavs.length) return;

  subNavs.forEach(subNav => setSubNavFocusable(subNav, focusable));
}

/**
 * Updates a parent item's submenu focusability when the submenu exists.
 *
 * @param {Element|null} subNavParent - The parent item containing the sub-nav
 * @param {boolean} focusable - Whether elements should receive keyboard focus
 */
function setChildSubNavFocusable(subNavParent, focusable) {
  if (!subNavParent) return;

  const subNav = subNavParent.querySelector('.sub-nav');
  if (!subNav) return;

  setSubNavFocusable(subNav, focusable);
}

/**
 * Collapses a parent item's anchor when the anchor exists.
 *
 * @param {Element|null} subNavParent - The parent item containing the sub-nav
 * @returns {Element|null} The collapsed parent anchor
 */
function collapseSubNavParentAnchor(subNavParent) {
  if (!subNavParent) return null;

  const parentAnchor = subNavParent.querySelector('.parent-anchor');
  if (!parentAnchor) return null;

  parentAnchor.setAttribute('aria-expanded', 'false');
  return parentAnchor;
}

/**
 * Closes a sub-navigation item and returns its parent anchor.
 *
 * @param {Element|null} subNavParent - The parent item containing the sub-nav
 * @returns {Element|null} The parent anchor when the item can be closed
 */
function closeSubNav(subNavParent) {
  if (!subNavParent) return null;

  subNavParent.classList.remove('sub-nav-active');
  setChildSubNavFocusable(subNavParent, false);
  return collapseSubNavParentAnchor(subNavParent);
}

/**
 * Closes the active sub-navigation item when one exists.
 */
function closeActiveSubNav() {
  const activeSubNavParent = document.querySelector('.sub-nav-active');
  if (!activeSubNavParent) return;

  closeSubNav(activeSubNavParent);
}

/**
 * Moves focus when the navigation opens or closes if its target exists.
 *
 * @param {boolean} isOpen - Whether the navigation is opening
 */
function updateNavFocus(isOpen) {
  if (isOpen) {
    if (!nav) return;

    const focusTarget = nav.querySelector('.nav-trigger-btn.close');
    if (!focusTarget) return;
    trapFocus(nav, focusTarget);
    return;
  }

  const focusTarget = document.querySelector('.nav-trigger-btn:not(.close)');
  if (!focusTarget) return;
  focusTarget.focus();
}

/**
 * Reports whether the mobile navigation is open when its header exists.
 *
 * @returns {boolean} Whether the mobile navigation is open
 */
function isMobileNavOpen() {
  if (!sectionHeader) return false;

  return sectionHeader.classList.contains('nav-active');
}

/**
 * Resets the open desktop submenu elements when they exist.
 */
function closeOpenDesktopSubNav() {
  const parentAnchor = document.querySelector('.parent-anchor.open');
  if (!parentAnchor) return;

  parentAnchor.classList.remove('open');
  parentAnchor.setAttribute('aria-expanded', 'false');
  parentAnchor.focus();

  const subNav = parentAnchor.nextElementSibling;
  if (!subNav) return;
  subNav.setAttribute('aria-hidden', 'true');
}

/**
 * Toggles the main navigation state for mobile.
 *
 * This function manages the addition and removal of the `overlay-open`,
 * `lock-scroll`, and `nav-active` classes based on the navigation state.
 *
 * @param {boolean} isOpen - If `true`, opens the navigation by adding
 * necessary classes to lock scrolling and show the overlay. If `false`,
 * closes the navigation by removing these classes.
 */
function toggleNavState(isOpen) {
  if (!sectionHeader || !navWrapper) return;

  document.body.classList.toggle('overlay-open', isOpen);
  document.body.classList.toggle('lock-scroll', isOpen);
  sectionHeader.classList.toggle('nav-active', isOpen);
  if (isOpen) {
    updateNavFocus(true);
    // Keep all sub-navs out of the tab order until explicitly opened
    setNavigationSubNavFocusable(false);
  } else {
    closeActiveSubNav();
    // Restore sub-nav elements so the DOM is clean after nav closes
    setNavigationSubNavFocusable(true);
    // Return focus to the trigger button outside the nav
    updateNavFocus(false);
  }
  navWrapper.setAttribute('aria-hidden', !isOpen);
}

/**
 * Handles the click or keydown event on the hamburger button.
 * Toggles the navigation menu and body scroll lock.
 *
 * @param {Event} e - The event object (click or keydown).
 */
function handleHamburger(e) {
  if (!sectionHeader) return;
  e.preventDefault();
  const isNavActive = sectionHeader.classList.contains('nav-active');
  toggleNavState(!isNavActive);
}

/**
 * Handles the click or keydown event on a parent anchor in mobile view.
 * Toggles the sub-navigation for the clicked parent item.
 *
 * @param {Event} e - The event object (click or keydown).
 */
function handleParentAnchorClick(e) {
  if (smallTabletAndUp.matches) return;

  e.preventDefault();
  e.stopPropagation();
  const parentLi = e.target.closest('.item-parent');
  if (!parentLi) return;

  const anchor = parentLi.querySelector('.parent-anchor');
  if (!anchor) return;
  const subNav = parentLi.querySelector('.sub-nav');
  if (!subNav) return;
  const backAnchor = subNav.querySelector('.back-anchor');
  if (!backAnchor) return;

  // Close any currently open sub-navs and remove them from tab order
  closeActiveSubNav();
  parentLi.classList.add('sub-nav-active');
  anchor.setAttribute('aria-expanded', 'true');

  // Make this sub-nav focusable, trap focus inside it, and move focus to the back button
  setSubNavFocusable(subNav, true);
  trapFocus(subNav, backAnchor);
}

/**
 * Handles the click or keydown event on a back anchor button.
 * Collapses the sub-navigation by removing the `sub-nav-active` class.
 *
 * @param {Event} e - The event object (click or keydown).
 */
function handleBackAnchorClick(e) {
  e.preventDefault();
  e.stopPropagation();
  // Find the closest parent that has the `sub-nav-active` class and remove it
  const subNavParent = e.target.closest('.sub-nav-active');
  if (!subNavParent) return;

  const parentAnchor = closeSubNav(subNavParent);
  if (!parentAnchor || !nav) return;

  // Release sub-nav trap, restore focus trap to the full nav, and return focus to the parent anchor
  trapFocus(nav, parentAnchor);
}

/**
 * Closes the open navigation or desktop sub-navigation when Escape is pressed.
 *
 * @param {KeyboardEvent} e - The keyboard event
 */
function handleEscapeKey(e) {
  if (e.key !== 'Escape') return;

  if (isMobileNavOpen()) {
    if (!navWrapper) return;
    e.preventDefault();
    toggleNavState(false);
    return;
  }

  if (!header || !navWrapper) return;
  if (!header.classList.contains('open')) return;

  e.preventDefault();
  closeOpenDesktopSubNav();
  header.classList.remove('open');
  document.body.classList.remove('overlay-open');
  navWrapper.setAttribute('aria-hidden', 'true');
}

// Track whether desktop nav listeners are active
let desktopListenersActive = false;

/**
 * Removes stored desktop navigation listeners from a parent item.
 *
 * @param {Element|null} parent - The navigation parent item
 */
function removeNavParentListeners(parent) {
  if (!parent) return;

  const keydownHandler = parent._keydownHandler;
  parent.removeEventListener('mouseenter', parent._mouseenterHandler);
  parent.removeEventListener('mouseleave', parent._mouseleaveHandler);
  parent.removeEventListener('focusout', parent._focusoutHandler);

  delete parent._mouseenterHandler;
  delete parent._mouseleaveHandler;
  delete parent._keydownHandler;
  delete parent._focusoutHandler;

  const anchor = parent.querySelector('.parent-anchor');
  if (!anchor) return;
  anchor.removeEventListener('keydown', keydownHandler);
}

/**
 * Adds or removes event listeners for navigation parent items based on window width.
 */
function setupNavParentListeners() {
  if (!header || !navWrapper) return;

  const navParents = document.querySelectorAll('.nav__wrapper > .item-parent');
  if (!navParents.length) return;

  // Only make changes if we're crossing the breakpoint
  if (smallTabletAndUp.matches && !desktopListenersActive) {
    // Add desktop listeners
    navParents.forEach(parent => {
      const parentAnchor = parent.querySelector('.parent-anchor');
      const subNav = parent.querySelector('.sub-nav');

      if (!parentAnchor || !subNav) return;

      // Store handlers on the element so we can remove them later
      parent._mouseenterHandler = () => {
        parentAnchor.classList.add('open');
        parentAnchor.setAttribute('aria-expanded', 'true');
        header.classList.add('open');
        document.body.classList.add('overlay-open');
        navWrapper.setAttribute('aria-hidden', 'false');
      };

      parent._mouseleaveHandler = () => {
        parentAnchor.classList.remove('open');
        parentAnchor.setAttribute('aria-expanded', 'false');
        header.classList.remove('open');
        document.body.classList.remove('overlay-open');
        navWrapper.setAttribute('aria-hidden', 'true');
      };

      parent._keydownHandler = e => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        e.preventDefault();
        parentAnchor.classList.add('open');
        parentAnchor.setAttribute('aria-expanded', 'true');
        subNav.setAttribute('aria-hidden', 'false');
        header.classList.add('open');
        navWrapper.setAttribute('aria-hidden', 'false');
      };

      parent._focusoutHandler = e => {
        if (!parent.contains(e.relatedTarget)) {
          parentAnchor.classList.remove('open');
          parentAnchor.setAttribute('aria-expanded', 'false');
          subNav.setAttribute('aria-hidden', 'true');
          header.classList.remove('open');
        }
      };

      parent.addEventListener('mouseenter', parent._mouseenterHandler);
      parent.addEventListener('mouseleave', parent._mouseleaveHandler);
      parentAnchor.addEventListener('keydown', parent._keydownHandler);
      parent.addEventListener('focusout', parent._focusoutHandler);
    });
    desktopListenersActive = true;
  } else if (!smallTabletAndUp.matches && desktopListenersActive) {
    // Remove desktop listeners
    navParents.forEach(parent => {
      if (parent._mouseenterHandler) {
        removeNavParentListeners(parent);
      }
    });
    desktopListenersActive = false;
  }
}

function handleNavigationBreakpointChange() {
  setupNavParentListeners();

  if (smallTabletAndUp.matches) {
    closeActiveSubNav();
    setNavigationSubNavFocusable(true);
    removeTrapFocus();
    return;
  }

  if (!isMobileNavOpen()) return;

  setNavigationSubNavFocusable(false);
  updateNavFocus(true);
}

// Event listeners for hamburger menu toggle
if (navBtns.length > 0) {
  navBtns.forEach(btn => {
    btn.addEventListener('click', handleHamburger);
    btn.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        handleHamburger(e);
      }
    });
  });
}

// Event listeners for parent anchors in mobile view
if (parentAnchors.length > 0) {
  parentAnchors.forEach(function (anchor) {
    anchor.addEventListener('click', handleParentAnchorClick);
    anchor.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        handleParentAnchorClick(e);
      }
    });
  });
}

// Event listeners for parent anchors in mobile view
if (parentAnchors.length > 0) {
  parentAnchors.forEach(function (anchor) {
    anchor.addEventListener('click', handleParentAnchorClick);
    anchor.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        handleParentAnchorClick(e);
      }
    });
  });
}

// Event listeners for back anchor buttons
if (backAnchors.length > 0) {
  backAnchors.forEach(function (backAnchor) {
    backAnchor.addEventListener('click', handleBackAnchorClick);
    backAnchor.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        handleBackAnchorClick(e);
      }
    });
  });
}

// Close an open sub-navigation with the Escape key
document.addEventListener('keydown', handleEscapeKey);

// Call initially to set up the listeners
syncHeaderDomOrder();
setupNavParentListeners();
setupNavMenuCarouselDragging();

smallTabletAndUp.addEventListener('change', handleNavigationBreakpointChange);
twoRowHeader.addEventListener('change', syncHeaderDomOrder);

// Event listener for overlay to close nav when clicked
if (overlay && sectionHeader) {
  overlay.addEventListener('click', function () {
    if (sectionHeader.classList.contains('nav-active')) {
      toggleNavState(false);
    }
  });
}
